/**
 * ResourceDirectory — the Clockify-synced people directory table + "Sync from Clockify" action.
 *
 * Shared by the standalone /resources page and the Settings → Resources tab so both entry points
 * render identical content. Lists resources (name, email, role/title, active status) with a
 * name/email search box, server-side pagination (20/page), and a per-row edit modal for
 * admin/leadership. The sync button triggers POST /api/resources/sync and is shown only to admins.
 */
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  RefreshCw,
  Search,
  Users,
  CheckCircle2,
  XCircle,
  Loader2,
  AlertTriangle,
  HelpCircle,
} from 'lucide-react';

import { useResources, useSyncResources, useUpdateResource } from '@/hooks/useResources';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { sortLevelOptions, sortTextOptions } from '@/lib/filter-options';
import { resolveResourceName } from '@/lib/resource-name';
import type { Resource } from '@/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { Tooltip } from '@/components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const PAGE_SIZE = 20;
const EDIT_ROLES = ['admin', 'leadership'];

/** Sentinel for "no filter applied". Shared by every toolbar Select. */
const ALL = 'all';

/**
 * Filter value sets. Declared in source order and ordered for display by the `sortTextOptions` /
 * `sortLevelOptions` helpers at the call site — never hand-ordered, so a value added here cannot
 * land out of order (RES-06, same convention as the Projects toolbar).
 *
 * Values mirror the Clockify custom fields documented in
 * packages/resources/services/clockify.service.ts (`extractLocation`, `extractEmploymentDesignation`,
 * `extractLevel`) and confirmed against docs/clockify_users_sample.json.
 */
const LOCATION_FILTER_VALUES: readonly string[] = ['Onshore', 'Offshore'];
const EMPLOYMENT_FILTER_VALUES: readonly string[] = ['Employee', 'Contractor'];
const LEVEL_FILTER_VALUES: readonly string[] = ['Junior', 'Mid', 'Senior'];

export interface ResourceDirectoryProps {
  /** When true, render the section heading + description (standalone page). Off inside a tab. */
  showHeader?: boolean;
}

/** Outcome of the most recent sync attempt in this session. See {@link SyncFreshness}. */
interface SyncState {
  status: 'success' | 'error';
  at: string;
  /** Rows synced — only present on a successful run. */
  synced?: number;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

// ─── Sync freshness (RES-04) ───────────────────────────────────────────────────

/**
 * Clockify sync freshness indicator.
 *
 * BACKEND GAP — NOT faked client-side: `GET /api/resources` does not return a last-sync timestamp.
 * `resources.updated_at` is set by every sync run (packages/resources/handlers/sync-clockify.ts) but
 * it is not in the SELECT list of packages/resources/handlers/list-resources.ts, nor on the
 * `Resource` contract in packages/shared/types/resources.ts. Until a `last_synced_at` (or
 * `updated_at`) field is added to that response, the only sync time this component can state
 * truthfully is one observed in the current browser session — so the "unknown" case says so plainly
 * instead of inventing a timestamp.
 */
function SyncFreshness({ state }: { state: SyncState | null }): JSX.Element {
  if (state?.status === 'success') {
    return (
      <p className="flex items-center gap-1.5 text-xs text-text-secondary">
        <CheckCircle2 className="h-3.5 w-3.5 text-success" aria-hidden="true" />
        Clockify sync succeeded {formatTime(state.at)} — {state.synced} resource
        {state.synced === 1 ? '' : 's'}.
      </p>
    );
  }

  if (state?.status === 'error') {
    return (
      <p className="flex items-center gap-1.5 text-xs text-error" role="status">
        <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
        Clockify sync failed {formatTime(state.at)} — the directory below may be out of date.
      </p>
    );
  }

  return (
    <p className="flex items-center gap-1.5 text-xs text-text-secondary">
      <Tooltip
        text="The resources API does not return a last-sync timestamp yet, so freshness can only be shown after a sync run in this session."
        width="wide"
      >
        <span tabIndex={0} className="inline-flex items-center gap-1.5 rounded focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary">
          <HelpCircle className="h-3.5 w-3.5" aria-hidden="true" />
          Last Clockify sync: unknown
        </span>
      </Tooltip>
    </p>
  );
}

// ─── Edit modal ────────────────────────────────────────────────────────────────

function EditResourceDrawer({
  resource,
  open,
  onOpenChange,
}: {
  resource: Resource | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const updateResource = useUpdateResource();
  const [name, setName] = useState('');
  const [roleTitle, setRoleTitle] = useState('');
  const [isActive, setIsActive] = useState(true);

  /** Classification of the STORED name (not the in-progress edit) — see resolveResourceName. */
  const storedName = resolveResourceName(resource ?? {});

  useEffect(() => {
    if (resource) {
      setName(resource.name ?? '');
      setRoleTitle(resource.role_title ?? '');
      setIsActive(resource.is_active);
    }
  }, [resource]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onOpenChange(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  const handleSave = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!resource) return;
    if (!name.trim()) { toast.error('Name is required.'); return; }
    try {
      await updateResource.mutateAsync({
        id: resource.id,
        input: {
          name: name.trim(),
          role_title: roleTitle.trim() === '' ? null : roleTitle.trim(),
          is_active: isActive,
        },
      });
      toast.success('Resource updated.');
      onOpenChange(false);
    } catch { /* global interceptor shows error toast */ }
  };

  if (!open && !resource) return <></>;

  return (
    <>
      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/30"
          onClick={() => onOpenChange(false)}
          aria-hidden="true"
        />
      )}

      {/* Drawer panel */}
      <div
        className={cn(
          'fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col bg-white shadow-xl transition-transform duration-200 ease-in-out',
          open ? 'translate-x-0' : 'translate-x-full',
        )}
        role="dialog"
        aria-modal="true"
        aria-label="Edit resource"
      >
        {/* Header */}
        <div className="flex items-start justify-between border-b border-outlines px-6 py-4">
          <div>
            <h2 className="text-base font-semibold text-text-primary">Edit resource</h2>
            <p className="mt-0.5 text-sm text-text-secondary">
              Name and role/title are preserved across Clockify syncs.
            </p>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label="Close"
            className="ml-4 flex h-8 w-8 items-center justify-center rounded-md text-text-secondary transition-colors hover:bg-subtle-bg"
          >
            <XCircle className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          <form id="edit-resource-form" onSubmit={(e) => void handleSave(e)} className="space-y-4">
            <div className="space-y-1.5">
              <Input label="Name" id="edit-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" />
              {/* RES-01: the stored name is a Clockify account identifier, not a person's name —
                  say so here, since this field is the only way to correct it (syncs preserve it). */}
              {storedName.isFallback && (
                <p className="text-xs text-text-secondary">{storedName.hint}</p>
              )}
            </div>
            <Input label="Role / Title" id="edit-role" value={roleTitle} onChange={(e) => setRoleTitle(e.target.value)} placeholder="e.g. Solutions Architect" />

            <div className="flex items-center gap-2">
              <input
                id="edit-active"
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="h-4 w-4 rounded border-outlines text-primary"
              />
              <label htmlFor="edit-active" className="cursor-pointer text-sm font-medium text-text-primary">Active</label>
            </div>

            <div className="border-t border-outlines pt-4">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-text-secondary">From Clockify (read-only)</p>
              <div className="space-y-3">
                <Input label="Email" id="edit-email" value={resource?.email ?? '—'} readOnly disabled />
                <Input label="Clockify ID" id="edit-clockify" value={resource?.clockify_user_id ?? '—'} readOnly disabled />
                <Input label="Designation" id="edit-designation" value={resource?.designation ?? '—'} readOnly disabled />
                <Input label="Status" id="edit-clockify-status" value={resource?.clockify_status ?? '—'} readOnly disabled />
                <div className="grid grid-cols-2 gap-3">
                  <Input label="Level" value={resource?.level ?? '—'} readOnly disabled />
                  <Input label="Location" value={resource?.location ?? '—'} readOnly disabled />
                  <Input label="Employment" value={resource?.employment_designation ?? '—'} readOnly disabled />
                  <Input label="Timesheets" value={resource?.submits_timesheets === false ? 'No' : resource?.submits_timesheets === true ? 'Yes' : '—'} readOnly disabled />
                </div>
              </div>
            </div>

            {resource?.practice_alignment?.length ? (
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-text-primary">Practice Alignment</label>
                <div className="flex flex-wrap gap-1 rounded-lg border border-outlines bg-subtle-bg px-3 py-2">
                  {resource.practice_alignment.map((p) => <Badge key={p} variant="info" className="text-xs">{p}</Badge>)}
                </div>
              </div>
            ) : null}

            {resource?.skill_area?.length ? (
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-text-primary">Skill Areas</label>
                <div className="flex flex-wrap gap-1 rounded-lg border border-outlines bg-subtle-bg px-3 py-2">
                  {resource.skill_area.map((s) => <Badge key={s} variant="neutral" className="text-xs">{s}</Badge>)}
                </div>
              </div>
            ) : null}

            {resource?.pod_assignment?.length ? (
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-text-primary">POD Assignment</label>
                <div className="flex flex-wrap gap-1 rounded-lg border border-outlines bg-subtle-bg px-3 py-2">
                  {resource.pod_assignment.map((p) => <Badge key={p} variant="neutral" className="text-xs">{p}</Badge>)}
                </div>
              </div>
            ) : null}
          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-outlines px-6 py-4">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={updateResource.isPending}>
            Cancel
          </Button>
          <Button type="submit" form="edit-resource-form" disabled={updateResource.isPending}>
            {updateResource.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            {updateResource.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </>
  );
}

// ─── Directory ───────────────────────────────────────────────────────────────

export function ResourceDirectory({ showHeader = false }: ResourceDirectoryProps): JSX.Element {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canEdit = EDIT_ROLES.includes(user?.role ?? '');

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [filterActive, setFilterActive] = useState<boolean | undefined>(undefined); // undefined = all
  const [filterTimesheets, setFilterTimesheets] = useState<boolean | undefined>(undefined);
  const [filterLocation, setFilterLocation] = useState<string>('all');
  const [filterEmployment, setFilterEmployment] = useState<string>('all');
  const [filterLevel, setFilterLevel] = useState<string>('all');
  const [syncState, setSyncState] = useState<SyncState | null>(null);
  const [editing, setEditing] = useState<Resource | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset to page 1 whenever the (debounced) search term changes.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const { data, isLoading, isError, refetch, isFetching } = useResources({
    search: debouncedSearch,
    page,
    page_size: PAGE_SIZE,
    active: filterActive,
  });
  const syncResources = useSyncResources();

  const resources = useMemo(() => {
    let list = data?.data ?? [];
    if (filterTimesheets === true) list = list.filter((r) => r.submits_timesheets === true);
    if (filterTimesheets === false) list = list.filter((r) => r.submits_timesheets !== true);
    if (filterLocation !== 'all') list = list.filter((r) => r.location === filterLocation);
    if (filterEmployment !== 'all')
      list = list.filter((r) => r.employment_designation === filterEmployment);
    if (filterLevel !== 'all') list = list.filter((r) => r.level === filterLevel);
    return list;
  }, [data, filterTimesheets, filterLocation, filterEmployment, filterLevel]);
  const total = data?.total ?? 0;
  const totalPages = data?.total_pages ?? 0;

  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);

  const handleSync = async (): Promise<void> => {
    try {
      const result = await syncResources.mutateAsync();
      setSyncState({ status: 'success', at: new Date().toISOString(), synced: result.synced });
      toast.success(
        `Clockify sync complete — ${result.synced} synced (${result.created} new, ${result.updated} updated).`,
      );
    } catch {
      // The global axios interceptor already surfaces an error toast (e.g. 403 / 502). Record the
      // failure so the freshness line can show a distinct stale/failed state (RES-04).
      setSyncState({ status: 'error', at: new Date().toISOString() });
    }
  };

  const openEdit = (resource: Resource): void => {
    setEditing(resource);
    setEditOpen(true);
  };

  const syncButton = isAdmin ? (
    <Button onClick={() => void handleSync()} disabled={syncResources.isPending} className="gap-2">
      <RefreshCw className={syncResources.isPending ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} aria-hidden="true" />
      {syncResources.isPending ? 'Syncing…' : 'Sync from Clockify'}
    </Button>
  ) : null;

  return (
    <div className="space-y-4">
      {showHeader && (
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold text-neutral-900">Resources</h1>
              <Badge variant="neutral">{total}</Badge>
            </div>
            <p className="text-sm text-text-secondary">
              People synced from Clockify, available for project assignment.
            </p>
          </div>
          {syncButton}
        </div>
      )}

      {/* Toolbar: search + labelled filters + (in-tab) sync button.
          Every filter carries a persistent visible field label (RES-02) — the closed control used to
          show only its value ("All staff", "All types"), which did not say what field it filtered.
          Options are ordered by the shared helpers, never by hand (RES-06): "All …" first, text
          values A–Z, Level by seniority rank.
          The `placeholder` prop is deliberately NOT passed: Select renders a placeholder as an extra
          clickable row that emits '', which these handlers read as a real value (e.g. `filterActive`
          would become `false` → "Inactive only"). The visible label replaces it. */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400"
            aria-hidden="true"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            aria-label="Search resources"
            className="h-[42px] pl-9 bg-white border-outlines"
          />
        </div>
        <Select
          id="filter-staff-status"
          label="Staff status"
          value={filterActive === undefined ? ALL : filterActive ? 'active' : 'inactive'}
          onChange={(v) => {
            setFilterActive(v === ALL ? undefined : v === 'active');
            setPage(1);
          }}
          className="w-[150px]"
          options={[
            { value: ALL, label: 'All' },
            ...sortTextOptions(['Active only', 'Inactive only']).map((label) => ({
              value: label.startsWith('Active') ? 'active' : 'inactive',
              label,
            })),
          ]}
        />

        {/* Timesheet submission — filters `submits_timesheets` ("Time Submission" in Clockify).
            KNOWN UX GAP: there is no Timesheets column in the table below, so this filter changes
            the row set on a field the user cannot see (it is visible only in the edit drawer's
            read-only "From Clockify" block). Labelled accurately here; surfacing the column is a
            separate change. */}
        <Select
          id="filter-timesheet-submission"
          label="Timesheet submission"
          value={filterTimesheets === undefined ? ALL : filterTimesheets ? 'yes' : 'no'}
          onChange={(v) => {
            setFilterTimesheets(v === ALL ? undefined : v === 'yes');
            setPage(1);
          }}
          className="w-[190px]"
          options={[
            { value: ALL, label: 'All' },
            ...sortTextOptions(['Submits timesheets', 'Does not submit']).map((label) => ({
              value: label === 'Submits timesheets' ? 'yes' : 'no',
              label,
            })),
          ]}
        />

        {/* Location filter */}
        <Select
          id="filter-location"
          label="Location"
          value={filterLocation}
          onChange={(v) => {
            setFilterLocation(v);
            setPage(1);
          }}
          className="w-[150px]"
          options={[
            { value: ALL, label: 'All' },
            ...sortTextOptions(LOCATION_FILTER_VALUES).map((v) => ({ value: v, label: v })),
          ]}
        />

        {/* Employment filter */}
        <Select
          id="filter-employment-type"
          label="Employment type"
          value={filterEmployment}
          onChange={(v) => {
            setFilterEmployment(v);
            setPage(1);
          }}
          className="w-[160px]"
          options={[
            { value: ALL, label: 'All' },
            ...sortTextOptions(EMPLOYMENT_FILTER_VALUES).map((v) => ({ value: v, label: v })),
          ]}
        />

        {/* Level filter — seniority order, not A–Z (see sortLevelOptions). */}
        <Select
          id="filter-level"
          label="Level"
          value={filterLevel}
          onChange={(v) => {
            setFilterLevel(v);
            setPage(1);
          }}
          className="w-[130px]"
          options={[
            { value: ALL, label: 'All' },
            ...sortLevelOptions(LEVEL_FILTER_VALUES).map((v) => ({ value: v, label: v })),
          ]}
        />
        {!showHeader && syncButton}
      </div>

      {/* Clockify sync freshness (RES-04). */}
      <SyncFreshness state={syncState} />

      {/* Error */}
      {isError && !isLoading && (
        <Card>
          <CardContent className="flex flex-col items-start gap-3 py-6">
            <p className="text-sm text-error">Failed to load resources.</p>
            <Button variant="outline" size="sm" onClick={() => void refetch()} className="gap-2">
              <RefreshCw className={isFetching ? 'h-3.5 w-3.5 animate-spin' : 'h-3.5 w-3.5'} aria-hidden="true" />
              Retry
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Loading */}
      {isLoading && (
        <Card>
          <CardContent className="space-y-3 p-5">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Empty */}
      {!isLoading && !isError && resources.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center gap-4 px-6 py-16 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Users className="h-7 w-7" aria-hidden="true" />
            </div>
            <div className="space-y-1">
              <h3 className="text-lg font-semibold text-text-primary">
                {debouncedSearch ? 'No matching resources' : 'No resources yet'}
              </h3>
              <p className="max-w-sm text-sm text-text-secondary">
                {debouncedSearch
                  ? 'Try a different name or email.'
                  : isAdmin
                    ? 'Sync from Clockify to populate the resource directory.'
                    : 'Ask an admin to sync the resource directory from Clockify.'}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      {!isLoading && !isError && resources.length > 0 && (
        <>
          <div className="rounded-lg border border-neutral-200 bg-white overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead className="hidden md:table-cell">Designation</TableHead>
                  <TableHead className="hidden lg:table-cell">Level</TableHead>
                  <TableHead className="hidden lg:table-cell">Location</TableHead>
                  <TableHead className="hidden xl:table-cell">Employment</TableHead>
                  <TableHead className="hidden xl:table-cell">Practice</TableHead>
                  <TableHead className="hidden 2xl:table-cell">POD</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {resources.map((r) => {
                  // RES-01: `resources.name` can hold a raw Clockify account identifier rather than
                  // a person's name. Never render that as if it were a verified name.
                  const displayName = resolveResourceName(r);
                  return (
                    <TableRow
                      key={r.id}
                      className={cn(
                        'border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50 transition-colors',
                        canEdit && 'cursor-pointer'
                      )}
                      onClick={canEdit ? () => openEdit(r) : undefined}
                    >
                      <TableCell>
                        {displayName.isFallback ? (
                          <Tooltip text={displayName.hint} width="wide">
                            <span
                              tabIndex={0}
                              title={displayName.hint}
                              className="rounded italic text-text-secondary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
                            >
                              {displayName.label}
                            </span>
                          </Tooltip>
                        ) : (
                          <span className="font-medium text-text-primary">{displayName.label}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-text-secondary text-sm">{r.email ?? '—'}</TableCell>

                      {/* Designation */}
                      <TableCell className="hidden text-text-secondary md:table-cell">
                        {r.designation ?? r.role_title ?? '—'}
                      </TableCell>

                      {/* Level */}
                      <TableCell className="hidden lg:table-cell">
                        {r.level ? (
                          <Badge variant="neutral" className="text-xs">
                            {r.level}
                          </Badge>
                        ) : (
                          <span className="text-text-secondary text-sm">—</span>
                        )}
                      </TableCell>

                      {/* Location */}
                      <TableCell className="hidden text-text-secondary text-sm lg:table-cell">
                        {r.location ?? '—'}
                      </TableCell>

                      {/* Employment */}
                      <TableCell className="hidden text-text-secondary text-sm xl:table-cell">
                        {r.employment_designation ?? '—'}
                      </TableCell>

                      {/* Practice Alignment */}
                      <TableCell className="hidden xl:table-cell">
                        <div className="flex flex-wrap gap-1">
                          {r.practice_alignment?.length ? (
                            r.practice_alignment.map((p) => (
                              <Badge key={p} variant="info" className="text-xs px-1.5 py-0">
                                {p}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-text-secondary text-sm">—</span>
                          )}
                        </div>
                      </TableCell>

                      {/* POD */}
                      <TableCell className="hidden 2xl:table-cell">
                        <div className="flex flex-wrap gap-1">
                          {r.pod_assignment?.length ? (
                            r.pod_assignment.map((p) => (
                              <Badge key={p} variant="neutral" className="text-xs px-1.5 py-0">
                                {p}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-text-secondary text-sm">—</span>
                          )}
                        </div>
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        {r.is_active ? (
                          <Badge variant="success" className="gap-1">
                            <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="neutral" className="gap-1">
                            <XCircle className="h-3 w-3" aria-hidden="true" />
                            Inactive
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          <div className="flex flex-col items-center justify-between gap-3 sm:flex-row">
            <p className="text-sm text-text-secondary">
              Showing {rangeStart}–{rangeEnd} of {total} resource{total === 1 ? '' : 's'}
            </p>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || isFetching}
              >
                Previous
              </Button>
              <span className="text-sm text-text-secondary">
                Page {page} of {Math.max(1, totalPages)}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= totalPages || isFetching}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}

      {canEdit && (
        <EditResourceDrawer resource={editing} open={editOpen} onOpenChange={setEditOpen} />
      )}
    </div>
  );
}

export default ResourceDirectory;
